import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  createComponent,
  DestroyableInjector,
  DestroyRef,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  Injector,
  input,
  InputSignal,
  Signal,
  Type,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PREVIEW_TOKEN } from '../../../../constants/injectionTokens.constant';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { MockCorporateDesignService } from '../../../../services/corporate-design/mock-corporate-design.service';
import { HeaderStore } from '../../../../services/api/header/header-store.service';
import { MockHeaderStore } from '../../../../services/api/header/mock-header-store.service';
import { BannerStore } from '../../../../services/api/banner/banner-store.service';
import { MockBannerStore } from '../../../../services/api/banner/mock-banner-store.service';
import { FooterStore } from '../../../../services/api/footer/footer-store.service';
import { MockFooterStore } from '../../../../services/api/footer/mock-footer-store.service';
import { PaymentProviderStore } from '../../../../services/api/payment-provider/payment-provider-store.service';
import { MockPaymentProviderStore } from '../../../../services/api/payment-provider/mock-payment-provider-store.service';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { MockUserStore } from '../../../../services/api/user/user/mock-user-store.service';
import { HeaderInteractionService } from '../../../../services/api/header/header-interaction.service';
import { MockHeaderInteractionService } from '../../../../services/api/header/mock-header-interaction.service';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

const STEP = 0.1;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2;

@Component({
  selector: 'aw-preview-content',
  imports: [CommonModule, FormsModule],
  templateUrl: './preview-content.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewContentComponent implements AfterViewInit {
  public components: InputSignal<Type<any>[]> = input.required();
  public isMobile: InputSignal<boolean> = input.required();
  public previewData: InputSignal<any[]> = input.required();

  private appRef: ApplicationRef = inject(ApplicationRef);
  private destroyRef: DestroyRef = inject(DestroyRef);

  @ViewChild('iframe', { static: true })
  iframeRef!: ElementRef<HTMLIFrameElement>;

  readonly environmentInjector: EnvironmentInjector =
    inject(EnvironmentInjector);

  public zoom: number = 1;
  public currentIndex: number = 0;

  private doc?: Document;
  private container?: HTMLElement;
  private compRef?: ComponentRef<unknown>;

  readonly mockInjector: Signal<DestroyableInjector> = computed(() =>
    Injector.create({
      providers: [
        {
          provide: PREVIEW_TOKEN,
          useValue: this.previewData()[this.currentIndex],
        },
        {
          provide: CorporateDesignService,
          useClass: MockCorporateDesignService,
        },
        {
          provide: HeaderStore,
          useClass: MockHeaderStore,
        },
        {
          provide: BannerStore,
          useClass: MockBannerStore,
        },
        {
          provide: FooterStore,
          useClass: MockFooterStore,
        },
        {
          provide: PaymentProviderStore,
          useClass: MockPaymentProviderStore,
        },
        {
          provide: UserStore,
          useClass: MockUserStore,
        },
        {
          provide: HeaderInteractionService,
          useClass: MockHeaderInteractionService,
        },
        {
          provide: DialogRef,
          useValue: {
            close: (): void => {},
          },
        },
        {
          provide: DIALOG_DATA,
          useValue: this.previewData(),
        },
      ],
      parent: this.environmentInjector,
    })
  );

  constructor() {
    effect(() => {
      this.previewData();
      if (this.doc) {
        this.renderPreview();
      }
    });
  }

  ngAfterViewInit() {
    const iframe: HTMLIFrameElement = this.iframeRef.nativeElement;
    const doc: Document = iframe.contentDocument!;
    const header: HTMLElement = document.getElementsByTagName('head')[0];
    const stylesLink: HTMLLinkElement = header.getElementsByTagName('link')[0];
    const styleSheet: string | null = stylesLink.getAttribute('href');
    doc.open();
    doc.write(`
      <html>
        <head>
          <link rel="stylesheet" href="../${styleSheet || 'styles.css'}">
        </head>
        <body class="m-0 p-0 w-full"></body>
      </html>
    `);
    doc.close();

    this.doc = doc;
    this.renderPreview();

    this.destroyRef.onDestroy(() => {
      if (this.compRef) {
        this.appRef.detachView(this.compRef.hostView);
        this.compRef.destroy();
      }
    });
  }

  private renderPreview(): void {
    if (!this.doc) {
      return;
    }

    if (this.compRef) {
      this.appRef.detachView(this.compRef.hostView);
      this.compRef.destroy();
      this.compRef = undefined;
    }
    // Destroying a component removes its host node, so re-render into a fresh
    // container instead of reusing doc.body directly.
    this.container?.remove();
    this.container = this.doc.createElement('div');
    this.doc.body.appendChild(this.container);

    this.compRef = createComponent(this.components()[this.currentIndex], {
      environmentInjector: this.appRef.injector,
      elementInjector: this.mockInjector(),
      hostElement: this.container,
    });

    this.appRef.attachView(this.compRef.hostView);
  }

  zoomIn() {
    this.zoom = Math.min(this.zoom + STEP, MAX_ZOOM);
  }

  zoomOut() {
    this.zoom = Math.max(this.zoom - STEP, MIN_ZOOM);
  }

  next() {
    if (this.currentIndex < this.components().length - 1) this.currentIndex++;
  }

  prev() {
    if (this.currentIndex > 0) this.currentIndex--;
  }
}
